/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from "react";
import { Alert, Badge, ProgressBar } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronRight, faTrashAlt } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useAppSelector } from "pages/thermography/config/store";
import {
  removeFile,
  selectAllValidated,
  selectValidations,
  selectWizardFiles
} from "../dataImportWizardSlice";
import FileValidationSection from "../components/FileValidationSection";
import { getEquipmentColumns } from "../equipmentsSchema";
import { useTranslation } from "react-i18next";



export default function ValidateStep() {
  const { t } = useTranslation();
  const tt = (key: string, options?: Record<string, unknown>) => t(key, { ns: "translation", ...options });
  const dispatch = useAppDispatch();
  const files = useAppSelector(selectWizardFiles);
  const validations = useAppSelector(selectValidations);
  const allValidated = useAppSelector(selectAllValidated);

  const [open, setOpen] = useState<Record<string, boolean>>({});

  // abre automaticamente os inválidos / em execução
  useEffect(() => {
    const next: Record<string, boolean> = { ...open };
    files.forEach(f => {
      const v = validations[f.id];
      const invalid = v?.result && v.result.isValid === false;
      const running = v?.status === "running";
      if (invalid || running) next[f.id] = true;
      else if (next[f.id] === undefined) next[f.id] = false;
    });
    setOpen(next);
  }, [files, validations]);

  const statusBadge = (fileId: string) => {
    const v = validations[fileId];
    if (!v) return <Badge bg="secondary">{tt("pages.thermography.dataImportWizard.validate.status.pending")}</Badge>;
    if (v.status === "running") return <Badge bg="info">{tt("pages.thermography.dataImportWizard.validate.status.validating")}</Badge>;
    if (v.status === "failed") return <Badge bg="danger">{tt("pages.thermography.dataImportWizard.validate.status.error")}</Badge>;
    if (v.status === "succeeded") {
      return v.result?.isValid
        ? <Badge bg="success">{tt("pages.thermography.dataImportWizard.validate.status.valid")}</Badge>
        : <Badge bg="warning">{tt("pages.thermography.dataImportWizard.validate.status.withIssues")}</Badge>;
    }
    return <Badge bg="secondary">{tt("pages.thermography.dataImportWizard.validate.status.pending")}</Badge>;
  };

  return (
    <div>
      <h5 className="mb-2">{tt("pages.thermography.dataImportWizard.validate.title")}</h5>
      <p className="text-muted mb-3">
        {tt("pages.thermography.dataImportWizard.validate.description")}
      </p>

      {!files.length && (
        <Alert variant="subtle-warning">
          {tt("pages.thermography.dataImportWizard.validate.noFiles")}
        </Alert>
      )}

      {files.map(f => {
        const v = validations[f.id];
        const isOpen = !!open[f.id];
        const result = v?.result;

        return (
          <div key={f.id} className="border rounded bg-body mb-3">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="btn btn-sm p-0"
                  onClick={() => setOpen(prev => ({ ...prev, [f.id]: !prev[f.id] }))}
                  aria-label={tt("pages.thermography.dataImportWizard.common.expandCollapse")}
                >
                  <FontAwesomeIcon icon={isOpen ? faChevronDown : faChevronRight} />
                </button>

                <div className="fw-semibold">{f.name}</div>
              </div>

              <div className="d-flex align-items-center gap-2">
                {statusBadge(f.id)}

                <button
                  type="button"
                  className="btn btn-sm p-0 text-danger"
                  title={tt("pages.thermography.dataImportWizard.common.removeFile")}
                  onClick={() => dispatch(removeFile({ fileId: f.id }))}
                >
                  <FontAwesomeIcon icon={faTrashAlt} />
                </button>
              </div>
            </div>

            {/* Progress */}
            {v?.status === "running" && (
              <div className="px-3 py-2">
                <ProgressBar now={v.progress} />
              </div>
            )}

            {/* Body */}
            {isOpen && (
              <div className="p-3">
                {v?.status === "failed" && (
                  <Alert variant="subtle-danger" className="py-2">
                    {tt("pages.thermography.dataImportWizard.validate.validationFailed")} {v.error ?? tt("pages.thermography.dataImportWizard.validate.unknownError")}
                  </Alert>
                )}

                {result && (
                  <FileValidationSection
                    fileName={f.name}
                    fileId={f.id}
                    isValid={!!result.isValid}
                    rowsToFix={result.rowsToFix}
                    issues={result.issues}
                    columns={getEquipmentColumns()}
                  />
                )}

                {!result && v?.status !== "running" && (
                  <Alert variant="subtle-info" className="py-2 mb-0">
                    {tt("pages.thermography.dataImportWizard.validate.clickValidateHint")}
                  </Alert>
                )}
              </div>
            )}
          </div>
        );
      })}

      {!allValidated && !!files.length && (
        <div className="text-muted small mt-2">
          {tt("pages.thermography.dataImportWizard.validate.nextEnabledHint")}
        </div>
      )}
    </div>
  );
}
